import React, { useEffect, useMemo, useState } from "react";
import { SYM } from "../utils/textSymbols";
import "./SchoolSettingsModal.css";

function dayDescriptions(profile, cycleLength) {
  const result = {};
  for (let day = 1; day <= cycleLength; day++) {
    const label = (profile?.templates || []).find((item) => item.cycleDay === day && String(item.title).toLowerCase() === `day ${day}`);
    const activities = (profile?.templates || []).filter((item) => item.cycleDay === day && String(item.title).toLowerCase() !== `day ${day}`).map((item) => item.title);
    result[day] = profile?.dayDescriptions?.[day] || label?.notes || activities.join(", ") || "";
  }
  return result;
}

function formFor(person, profile) {
  const cycleLength = Math.min(7, Math.max(1, Number(profile?.cycleLength) || 6));
  return {
    id: profile?.id,
    child: person || profile?.child || "",
    school: profile?.school || "",
    schoolAliases: profile?.schoolAliases || "",
    schoolWebsite: profile?.schoolWebsite || "",
    cycleLength,
    descriptions: dayDescriptions(profile, cycleLength)
  };
}

export default function SchoolSettingsModal({ open, onClose, apiBase, familyMembers = [] }) {
  const people = useMemo(() => familyMembers.map((member) => typeof member === "object" ? member.name : member).filter((name) => name && name.toLowerCase() !== "all"), [familyMembers]);
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState(() => formFor("", null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(""); setMessage("");
    fetch(`${apiBase}/api/GetSchoolProfiles`, { credentials: "include" })
      .then(async (res) => { const data = await res.json(); if (!res.ok) throw new Error(data.error || `Error ${res.status}`); return data; })
      .then((data) => {
        const loaded = data.profiles || [];
        setProfiles(loaded);
        const person = people[0] || loaded[0]?.child || "";
        setForm(formFor(person, loaded.find((profile) => profile.child.toLowerCase() === person.toLowerCase())));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [apiBase, open, people]);

  if (!open) return null;
  const choosePerson = (child) => setForm(formFor(child, profiles.find((profile) => profile.child.toLowerCase() === child.toLowerCase())));
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const setCycleLength = (cycleLength) => setForm((current) => ({ ...current, cycleLength, descriptions: { ...dayDescriptions({ dayDescriptions: current.descriptions }, cycleLength), ...current.descriptions } }));
  const save = async () => {
    setError(""); setMessage("");
    if (!form.child || !form.school.trim()) { setError("Choose a person and enter a school name."); return; }
    setSaving(true);
    try {
      const existing = profiles.find((profile) => profile.id === form.id || profile.child.toLowerCase() === form.child.toLowerCase());
      const custom = (existing?.templates || []).filter((item) => item.cycleDay <= form.cycleLength && String(item.title).toLowerCase() !== `day ${item.cycleDay}`);
      const labels = Array.from({ length: form.cycleLength }, (_, index) => ({ cycleDay: index + 1, title: `Day ${index + 1}`, allDay: true, startTime: "00:00", endTime: "00:00", location: "", notes: form.descriptions[index + 1] || "" }));
      const profile = { id: form.id, child: form.child, school: form.school.trim(), schoolAliases: form.schoolAliases.trim(), schoolWebsite: form.schoolWebsite.trim(), cycleLength: form.cycleLength, dayDescriptions: form.descriptions, templates: [...labels, ...custom] };
      const res = await fetch(`${apiBase}/api/SaveSchoolProfile`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ profile }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setProfiles((current) => [...current.filter((item) => item.id !== data.profile.id && item.child.toLowerCase() !== data.profile.child.toLowerCase()), data.profile].sort((a, b) => a.child.localeCompare(b.child)));
      setForm(formFor(data.profile.child, data.profile));
      setMessage(`${data.profile.child}'s school configuration was saved.`);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  return <div className="settings-overlay" onClick={() => !saving && onClose()}>
    <div className="settings-modal school-settings-modal" onClick={(event) => event.stopPropagation()}>
      <div className="settings-modal-header"><h2>School settings</h2><button type="button" className="settings-close" onClick={onClose} disabled={saving} aria-label="Close">{SYM.close}</button></div>
      <div className="settings-modal-body">
        <p className="settings-hint">Assign a school and reusable rotation to any family member. Smart Add, forwarded emails, and closure adjustments use this configuration.</p>
        {loading ? <p>Loading…</p> : <>
          <label className="school-config-field">Person<select className="settings-input" value={form.child} onChange={(event) => choosePerson(event.target.value)}><option value="">Choose person</option>{people.map((person) => <option key={person} value={person}>{person}{profiles.some((profile) => profile.child.toLowerCase() === person.toLowerCase()) ? " — configured" : ""}</option>)}</select></label>
          <label className="school-config-field">School name<input className="settings-input" value={form.school} onChange={(event) => set("school", event.target.value)} placeholder="School name" /></label>
          <label className="school-config-field">School aliases <small>District name, abbreviation, or other names used in emails</small><input className="settings-input" value={form.schoolAliases} onChange={(event) => set("schoolAliases", event.target.value)} placeholder="Comma-separated aliases" /></label>
          <label className="school-config-field">School website<input type="url" className="settings-input" value={form.schoolWebsite} onChange={(event) => set("schoolWebsite", event.target.value)} placeholder="https://…" /></label>
          <label className="school-config-field">Days in rotation<select className="settings-input" value={form.cycleLength} onChange={(event) => setCycleLength(Number(event.target.value))}>{Array.from({ length: 7 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1} day{index ? "s" : ""}</option>)}</select></label>
          <div className="school-config-days"><h3>Rotation day descriptions</h3><p className="settings-hint">These labels remain available for future calendar fills. Descriptions are optional.</p>{Array.from({ length: form.cycleLength }, (_, index) => {
            const day = index + 1;
            return <label key={day} className="school-config-day"><span>Day {day}</span><textarea className="settings-input" rows={2} value={form.descriptions[day] || ""} onChange={(event) => setForm((current) => ({ ...current, descriptions: { ...current.descriptions, [day]: event.target.value } }))} placeholder={`Description for Day ${day}`} /></label>;
          })}</div>
          {error && <p className="settings-error">{error}</p>}{message && <p className="settings-success">{message}</p>}
          <button type="button" className="settings-submit" onClick={save} disabled={saving || !form.child}>{saving ? "Saving…" : "Save school configuration"}</button>
        </>}
      </div>
    </div>
  </div>;
}
